/**
 * Position Chart Component
 *
 * TradingView-style chart using lightweight-charts library
 * Shows position entry/exit points and key technical levels
 */

import { useEffect, useRef, useState } from 'react';
import { createChart, IChartApi, ISeriesApi, LineStyle, UTCTimestamp } from 'lightweight-charts';
import type { Position } from '../../services/aiConsensusAPI';

interface PositionChartProps {
  symbol: string;
  positions: Position[];
  height?: number;
  onPriceUpdate?: (price: number) => void;
}

// Generate mock candlestick data for demo
// In production, this would fetch from an API
function generateMockCandles(currentPrice: number, count: number = 100): Array<{
  time: UTCTimestamp;
  open: number;
  high: number;
  low: number;
  close: number;
}> {
  const candles = [];
  let basePrice = currentPrice * 0.95;
  const now = Math.floor(Date.now() / 1000);
  const interval = 3600; // 1 hour candles

  for (let i = count - 1; i >= 0; i--) {
    const volatility = 0.005;
    const open = basePrice;
    const change = (Math.random() - 0.5) * basePrice * volatility * 2;
    const close = open + change;
    const high = Math.max(open, close) + Math.random() * basePrice * volatility;
    const low = Math.min(open, close) - Math.random() * basePrice * volatility;

    candles.push({
      time: (now - i * interval) as UTCTimestamp,
      open,
      high,
      low,
      close,
    });

    basePrice = close;
  }

  // Ensure last candle ends at current price
  if (candles.length > 0) {
    candles[candles.length - 1].close = currentPrice;
  }

  return candles;
}

// Calculate Simple Moving Average
function calculateSMA(data: number[], period: number): number[] {
  const sma: number[] = [];
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      sma.push(NaN);
    } else {
      const sum = data.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0);
      sma.push(sum / period);
    }
  }
  return sma;
}

export function PositionChart({ symbol, positions, height = 400, onPriceUpdate }: PositionChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const isDisposedRef = useRef<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState(document.body.classList.contains('dark-mode'));

  // Get current price from first position or use a default
  const currentPrice = positions.length > 0 ? positions[0].currentPrice : 100000;

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains('dark-mode'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Reset disposed flag for new chart creation
    isDisposedRef.current = false;

    // Safely clear previous chart
    if (chartRef.current) {
      try {
        chartRef.current.remove();
      } catch (e) {
        // Chart already disposed, ignore
      }
      chartRef.current = null;
      candleSeriesRef.current = null;
    }

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height,
      layout: {
        background: { color: isDarkMode ? '#0d1117' : '#ffffff' },
        textColor: isDarkMode ? '#8b949e' : '#666666',
      },
      grid: {
        vertLines: { color: isDarkMode ? '#21262d' : '#f0f0f0' },
        horzLines: { color: isDarkMode ? '#21262d' : '#f0f0f0' },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderColor: isDarkMode ? '#30363d' : '#e0e0e0',
      },
      timeScale: {
        borderColor: isDarkMode ? '#30363d' : '#e0e0e0',
        timeVisible: true,
        secondsVisible: false,
      },
    });

    chartRef.current = chart;

    // Add candlestick series
    const candleSeries = chart.addCandlestickSeries({
      upColor: '#22c55e',
      downColor: '#ef4444',
      borderUpColor: '#22c55e',
      borderDownColor: '#ef4444',
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
    });
    candleSeriesRef.current = candleSeries;

    // Generate and set candle data
    const candles = generateMockCandles(currentPrice);
    candleSeries.setData(candles);

    // Calculate and add SMA lines
    const closes = candles.map(c => c.close);

    // 20 SMA (short-term)
    const sma20 = calculateSMA(closes, 20);
    const sma20Series = chart.addLineSeries({
      color: '#f59e0b',
      lineWidth: 1,
      title: 'SMA 20',
    });
    sma20Series.setData(
      sma20.map((value, i) => ({
        time: candles[i].time,
        value: isNaN(value) ? undefined : value,
      })).filter(d => d.value !== undefined) as Array<{ time: UTCTimestamp; value: number }>
    );

    // 50 SMA (medium-term)
    const sma50 = calculateSMA(closes, 50);
    const sma50Series = chart.addLineSeries({
      color: '#3b82f6',
      lineWidth: 1,
      title: 'SMA 50',
    });
    sma50Series.setData(
      sma50.map((value, i) => ({
        time: candles[i].time,
        value: isNaN(value) ? undefined : value,
      })).filter(d => d.value !== undefined) as Array<{ time: UTCTimestamp; value: number }>
    );

    // Calculate Fibonacci retracement levels
    const highs = candles.map(c => c.high);
    const lows = candles.map(c => c.low);
    const periodHigh = Math.max(...highs);
    const periodLow = Math.min(...lows);
    const fibRange = periodHigh - periodLow;

    // Fibonacci levels: 0%, 23.6%, 38.2%, 50%, 61.8%, 78.6%, 100%
    const fibLevels = [
      { level: 0, color: '#6366f1', label: '0%' },
      { level: 0.236, color: '#8b5cf6', label: '23.6%' },
      { level: 0.382, color: '#a855f7', label: '38.2%' },
      { level: 0.5, color: '#d946ef', label: '50%' },
      { level: 0.618, color: '#ec4899', label: '61.8%' },
      { level: 0.786, color: '#f43f5e', label: '78.6%' },
      { level: 1, color: '#ef4444', label: '100%' },
    ];

    // Add Fibonacci lines
    fibLevels.forEach(({ level, color }) => {
      const fibPrice = periodLow + (fibRange * level);
      const fibSeries = chart.addLineSeries({
        color,
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        crosshairMarkerVisible: false,
        lastValueVisible: false,
        priceLineVisible: false,
      });

      // Only show on last 30% of chart to not clutter
      const startIndex = Math.floor(candles.length * 0.7);
      const fibData = candles.slice(startIndex).map(c => ({
        time: c.time,
        value: fibPrice,
      }));
      fibSeries.setData(fibData);
    });

    // Add position markers
    positions.forEach((position) => {
      // Entry price line
      const entryLine = chart.addLineSeries({
        color: position.side === 'LONG' ? '#22c55e' : '#ef4444',
        lineWidth: 2,
        lineStyle: LineStyle.Dashed,
        title: `Entry ${position.symbol}`,
      });

      // Create horizontal line data
      const entryData = candles.map(c => ({
        time: c.time,
        value: position.entryPrice,
      }));
      entryLine.setData(entryData);

      // Current price line
      const currentLine = chart.addLineSeries({
        color: '#8b5cf6',
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        title: 'Current',
      });

      const currentData = candles.map(c => ({
        time: c.time,
        value: position.currentPrice,
      }));
      currentLine.setData(currentData);

      // Add markers for entry points
      candleSeries.setMarkers([
        {
          time: candles[candles.length - 10].time,
          position: position.side === 'LONG' ? 'belowBar' : 'aboveBar',
          color: position.side === 'LONG' ? '#22c55e' : '#ef4444',
          shape: position.side === 'LONG' ? 'arrowUp' : 'arrowDown',
          text: `${position.side} @ $${position.entryPrice.toFixed(2)}`,
        },
      ]);
    });

    // Handle resize - check if chart is not disposed
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current && !isDisposedRef.current) {
        try {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
          });
        } catch (e) {
          // Chart disposed during resize, ignore
        }
      }
    };

    window.addEventListener('resize', handleResize);

    // Fit content
    chart.timeScale().fitContent();

    // Report current price
    if (onPriceUpdate) {
      onPriceUpdate(currentPrice);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      isDisposedRef.current = true;
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (e) {
          // Chart already disposed, ignore
        }
        chartRef.current = null;
        candleSeriesRef.current = null;
      }
    };
  }, [symbol, positions, height, isDarkMode, currentPrice, onPriceUpdate]);

  return (
    <div
      ref={chartContainerRef}
      style={{
        width: '100%',
        height,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    />
  );
}

export default PositionChart;
